import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { customerAPI, careReportAPI, packageAPI } from '../api/axios';
import {
  FileText,
  PenTool,
  Calendar,
  Search,
  Save,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Camera,
  Upload,
  X,
  Plus
} from 'lucide-react';
import GuideCamera from '../components/GuideCamera';
import './CareReportPage.css';

const CareReportPage = () => {
  const location = useLocation();
  // 상태 관리
  const [searchTerm, setSearchTerm] = useState(''); // 고객 검색어
  const [searchResults, setSearchResults] = useState([]); // 검색 결과
  const [selectedCustomer, setSelectedCustomer] = useState(null); // 선택된 고객
  const [careHistory, setCareHistory] = useState([]); // 케어 히스토리
  const [content, setContent] = useState(''); // 관리 내용
  const [adminName] = useState('박현지'); // 담당 원장 (고정값)
  const [careDate, setCareDate] = useState(''); // 관리 일자
  const [isSearching, setIsSearching] = useState(false); // 검색 중 상태

  // 패키지 관련 상태
  const [usePackage, setUsePackage] = useState(false); // 패키지 사용 여부
  const [treatmentCost, setTreatmentCost] = useState(''); // 시술 비용
  const [availablePackages, setAvailablePackages] = useState([]); // 사용 가능한 패키지 목록
  const [selectedPackageId, setSelectedPackageId] = useState(''); // 선택된 패키지 ID
  const [usedCount, setUsedCount] = useState(1); // 차감할 횟수 (횟수권용)

  // 슬라이드 패널 상태
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 한 페이지당 10개

  const signaturePad = useRef(null); // 서명 패드 참조

  // 사진 관련 상태
  const [usePhoto, setUsePhoto] = useState(false); // 사진 사용 여부
  const [photos, setPhotos] = useState({
    front: null,   // 정면 사진 (Base64)
    left: null,    // 좌측 사진
    right: null    // 우측 사진
  });
  const [showCamera, setShowCamera] = useState(false); // 카메라 모달 표시
  const [showPhotoSelector, setShowPhotoSelector] = useState(false); // 촬영/업로드 선택 모달
  const [currentPhotoType, setCurrentPhotoType] = useState(null); // 'front' | 'left' | 'right'
  const fileInputRef = useRef(null); // 파일 업로드 input 참조

  // 컴포넌트 마운트 시 오늘 날짜 설정
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0]; // YYYY-MM-DD
    setCareDate(formattedDate);
  }, []);

  // ESC 키로 패널 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isPanelOpen) {
        handleClosePanel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isPanelOpen]);

  // 패널 열릴 때 body 스크롤 막기 (모바일)
  useEffect(() => {
    if (isPanelOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isPanelOpen]);

  // 예약 관리에서 넘어온 경우 자동 검색
  useEffect(() => {
    const autoSearchCustomer = async () => {
      // location.state에서 자동 검색 정보 확인
      if (location.state?.autoSearch && location.state?.customerPhone) {
        const { customerPhone, customerName } = location.state;

        console.log('[케어리포트] 자동 검색 시작:', customerPhone);

        // 검색어 설정 (전화번호로 검색)
        setSearchTerm(customerPhone);
        setIsSearching(true);

        try {
          const response = await customerAPI.search(customerPhone);
          console.log('[케어리포트] 자동 검색 결과:', response.data);

          if (response.data.length > 0) {
            // 전화번호가 정확히 일치하는 고객 찾기
            const matchedCustomer = response.data.find(
              customer => customer.phone === customerPhone
            ) || response.data[0]; // 없으면 첫 번째 결과

            // 고객 자동 선택
            await handleSelectCustomer(matchedCustomer);

            // 패널 자동 열기
            setIsPanelOpen(true);

            console.log('[케어리포트] 고객 자동 선택 완료:', matchedCustomer.name);
          } else {
            alert(`고객을 찾을 수 없습니다.\n이름: ${customerName}\n전화번호: ${customerPhone}`);
          }
        } catch (error) {
          console.error('[케어리포트] 자동 검색 실패:', error);
          alert('고객 검색에 실패했습니다.');
        } finally {
          setIsSearching(false);
        }

        // location.state 초기화 (뒤로가기 시 재실행 방지)
        window.history.replaceState({}, document.title);
      }
    };

    autoSearchCustomer();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // 페이지네이션 계산
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = careHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(careHistory.length / itemsPerPage);

  // 이전 페이지
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // 다음 페이지
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // 고객 검색 함수
  const handleSearch = async () => {
    console.log('[케어리포트] 검색 시작:', searchTerm);

    if (!searchTerm.trim()) {
      alert('검색어를 입력하세요.');
      return;
    }

    setIsSearching(true);

    try {
      console.log('[케어리포트] API 호출: /api/customers/search?query=' + searchTerm);

      const response = await customerAPI.search(searchTerm);

      console.log('[케어리포트] 검색 결과:', response.data);
      console.log('[케어리포트] 검색 결과 개수:', response.data.length);

      setSearchResults(response.data);

      if (response.data.length === 0) {
        alert('검색 결과가 없습니다.');
      }
    } catch (error) {
      console.error('[케어리포트] 고객 검색 실패:', error);
      console.error('[케어리포트] 에러 상세:', error.response);
      alert(`고객 검색에 실패했습니다.\n에러: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // 고객 선택 함수
  const handleSelectCustomer = async (customer) => {
    console.log('[케어리포트] 고객 선택:', customer);

    setSelectedCustomer(customer);
    setSearchResults([]); // 검색 결과 초기화
    setSearchTerm(''); // 검색어 초기화
    setCurrentPage(1); // 페이지 초기화

    // 선택된 고객의 케어 히스토리 조회
    try {
      console.log('[케어리포트] 히스토리 조회 API 호출: customerId=' + customer.id);

      const response = await careReportAPI.getAll(customer.id);

      console.log('[케어리포트] 히스토리 응답:', response.data);
      console.log('[케어리포트] 히스토리 개수:', response.data.length);

      setCareHistory(response.data);
    } catch (error) {
      console.error('[케어리포트] ���스토리 조회 실패:', error);
      console.error('[케어리포트] 에러 상세:', error.response);
      setCareHistory([]);
      alert(`히스토리 조회에 실패했습니다.\n에러: ${error.message}`);
    }

    // 사용 가능한 패키지 조회 (본인 + 공유받은)
    try {
      console.log('[케어리포트] 패키지 조회 API 호출: customerId=' + customer.id);
      const packagesRes = await packageAPI.getAvailablePackages(customer.id);
      console.log('[케어리포트] 패키지 응답 (본인+공유):', packagesRes.data);
      setAvailablePackages(packagesRes.data);
    } catch (error) {
      console.error('[케어리포트] 패키지 조회 실패:', error);
      setAvailablePackages([]);
    }
  };

  // 패널 열기
  const handleOpenPanel = () => {
    if (!selectedCustomer) {
      alert('고객을 먼저 선택하세요.');
      return;
    }
    setIsPanelOpen(true);
  };

  // 패널 닫기
  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // 서명 패드 초기화
  const handleClearSignature = () => {
    signaturePad.current.clear();
  };

  // 사진 촬영/업로드 선택 모달 열기
  const handleOpenPhotoSelector = (photoType) => {
    setCurrentPhotoType(photoType);
    setShowPhotoSelector(true);
  };

  // 카메라 촬영 선택
  const handleSelectCamera = () => {
    setShowPhotoSelector(false);
    setShowCamera(true);
  };

  // 파일 업로드 선택
  const handleSelectUpload = () => {
    setShowPhotoSelector(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 카메라로 촬영된 사진 처리
  const handlePhotoCapture = (imageSrc) => {
    setPhotos(prev => ({
      ...prev,
      [currentPhotoType]: imageSrc
    }));
    setShowCamera(false);
  };

  // 파일 업로드 처리
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 이미지 파일인지 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    // 파일 크기 제한 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('이미지 파일 크기는 10MB 이하로 제한됩니다.');
      return;
    }

    // Base64로 변환
    const reader = new FileReader();
    reader.onload = (event) => {
      setPhotos(prev => ({
        ...prev,
        [currentPhotoType]: event.target.result
      }));
    };
    reader.readAsDataURL(file);

    // input 초기화 (같은 파일 재선택 가능하도록)
    e.target.value = '';
  };

  // 사진 삭제
  const handleDeletePhoto = (photoType) => {
    setPhotos(prev => ({
      ...prev,
      [photoType]: null
    }));
  };

  // 선택된 패키지 정보 가져오기
  const getSelectedPackage = () => {
    return availablePackages.find(pkg => pkg.id === parseInt(selectedPackageId));
  };

  // 케어 리포트 저장
  const handleSave = async () => {
    console.log('[케어리포트] 저장 시작');

    // 검증
    if (!selectedCustomer) {
      alert('고객을 먼저 선택하세요.');
      return;
    }
    if (!content.trim()) {
      alert('관리 내용을 입력하세요.');
      return;
    }
    if (content.trim().length < 10) {
      alert('관리 내용은 최소 10자 이상 입력해야 합니다.\n현재: ' + content.trim().length + '자');
      return;
    }
    if (content.trim().length > 5000) {
      alert('관리 내용은 최대 5000자까지 입력 가능합니다.\n현재: ' + content.trim().length + '자');
      return;
    }
    if (signaturePad.current.isEmpty()) {
      alert('고객 서명을 받으세요.');
      return;
    }

    // 패키지 사용 시 추가 검증
    if (usePackage) {
      if (!selectedPackageId) {
        alert('사용할 패키지를 선택하세요.');
        return;
      }
      const pkg = getSelectedPackage();
      if (pkg.packageType === 'AMOUNT_BASED' && (!treatmentCost || treatmentCost <= 0)) {
        alert('시술 비용을 입력하세요.');
        return;
      }
    }

    try {
      // 서명을 Base64 이미지로 변환
      const signatureData = signaturePad.current.toDataURL();

      console.log('[케어리포트] 저장 데이터:', {
        customerId: selectedCustomer.id,
        contentLength: content.length,
        adminName: adminName,
        signatureLength: signatureData.length,
        usePackage: usePackage,
        packageId: selectedPackageId,
        treatmentCost: treatmentCost,
        usedCount: usedCount,
        hasPhotos: {
          front: !!photos.front,
          left: !!photos.left,
          right: !!photos.right
        }
      });

      let response;

      // 패키지 사용 여부에 따라 다른 API 호출
      if (usePackage && selectedPackageId) {
        response = await careReportAPI.createWithPackage({
          customerId: selectedCustomer.id,
          content: content,
          adminName: adminName,
          customerSignature: signatureData,
          treatmentCost: treatmentCost ? parseInt(treatmentCost) : null,
          usePackage: true,
          packageId: parseInt(selectedPackageId),
          usedCount: parseInt(usedCount),
          // 사진 추가 (선택 사항)
          photoFront: photos.front || null,
          photoLeft: photos.left || null,
          photoRight: photos.right || null
        });
      } else {
        response = await careReportAPI.create({
          customerId: selectedCustomer.id,
          content: content,
          adminName: adminName,
          customerSignature: signatureData,
          // 사진 추가 (선택 사항)
          photoFront: photos.front || null,
          photoLeft: photos.left || null,
          photoRight: photos.right || null
        });
      }

      console.log('[케어리포트] 저장 성공:', response.data);

      alert('케어 리포트가 저장되었습니다.');

      // 폼 초기화
      setContent('');
      signaturePad.current.clear();
      setUsePackage(false);
      setTreatmentCost('');
      setSelectedPackageId('');
      setUsedCount(1);
      setUsePhoto(false);
      setPhotos({ front: null, left: null, right: null }); // 사진 초기화

      // 히스토리 새로고침
      console.log('[케어리포트] 히스토리 새로고침 중...');

      const historyResponse = await careReportAPI.getAll(selectedCustomer.id);

      console.log('[케어리포트] 업데이트된 히스토리:', historyResponse.data);

      setCareHistory(historyResponse.data);
      setCurrentPage(1); // 첫 페이지로 이동

      // 패키지 목록도 새로고침 (본인 + 공유받은)
      if (usePackage) {
        const packagesRes = await packageAPI.getAvailablePackages(selectedCustomer.id);
        setAvailablePackages(packagesRes.data);
      }

      // 패널 닫기
      setIsPanelOpen(false);

    } catch (error) {
      console.error('[케어리포트] 저장 실패:', error);
      console.error('[케어리포트] 에러 상세:', error.response);

      // 에러 메시지 구성
      let errorMessage = '케어 리포트 저장에 실패했습니다.\n';

      if (error.response) {
        console.error('[케어리포트] 응답 상태:', error.response.status);
        console.error('[케어리포트] 응답 데이터:', error.response.data);

        // Validation 에러 처리
        if (error.response.data?.details) {
          errorMessage += '\n입력값 오류:\n';
          Object.entries(error.response.data.details).forEach(([field, message]) => {
            errorMessage += `- ${field}: ${message}\n`;
          });
        } else if (error.response.data?.message) {
          errorMessage += '\n' + error.response.data.message;
        }
      } else if (error.request) {
        errorMessage += '\n서버와 연결할 수 없습니다.';
      } else {
        errorMessage += '\n' + error.message;
      }

      alert(errorMessage);
    }
  };

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 텍스트 말줄임 함수
  const truncateText = (text, maxLength = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="care-report-page">
      {/* 히스토리 섹션 (메인 화면) */}
      <div className="history-section">
        <div className="section-header">
          <FileText size={24} className="section-icon" />
          <h2>케어 히스토리</h2>
        </div>

        {/* 고객 검색 */}
        <div className="form-group">
          <label>
            <Search size={18} className="label-icon" />
            고객 검색
          </label>
          <div className="customer-search">
            <input
              type="text"
              placeholder="고객 이름 또는 전화번호 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              disabled={isSearching}
            />
            <button
              onClick={handleSearch}
              className="search-button"
              disabled={isSearching}
            >
              <Search size={18} />
              {isSearching ? '검색중...' : '검색'}
            </button>

            {/* 검색 결과 */}
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((customer) => (
                  <div
                    key={customer.id}
                    className="search-result-item"
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    {customer.name} ({customer.phone})
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedCustomer ? (
          <>
            <div className="selected-customer-info">
              <h3>{selectedCustomer.name}</h3>
              <p>{selectedCustomer.phone}</p>
              <div className="history-count">
                총 {careHistory.length}건의 기록
              </div>
            </div>

            {/* 리포트 작성 버튼 */}
            <button className="btn-create-report" onClick={handleOpenPanel}>
              <Plus size={20} />
              리포트 작성
            </button>

            {careHistory.length === 0 ? (
              <div className="no-history">
                <FileText size={48} className="no-history-icon" />
                <p>기록된 히스토리가 없습니다.</p>
              </div>
            ) : (
              <>
                <div className="history-table-wrapper">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>날짜</th>
                        <th>관리 내용</th>
                        <th>원장 서명</th>
                        <th>고객 서명</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((report) => (
                        <tr key={report.id}>
                          <td className="date-cell">{formatDate(report.createdAt)}</td>
                          <td className="content-cell">
                            <div className="content-text" title={report.content}>
                              {truncateText(report.content, 60)}
                            </div>
                            <div className="admin-name">담당: {report.adminName}</div>
                          </td>
                          <td className="signature-cell">
                            {report.adminSignature ? (
                              <img
                                src={report.adminSignature}
                                alt="원장 서명"
                                className="signature-image"
                              />
                            ) : (
                              <div className="signature-label">{report.adminName}</div>
                            )}
                          </td>
                          <td className="signature-cell">
                            <img
                              src={report.customerSignature}
                              alt="고객 서명"
                              className="signature-image"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      이전
                    </button>

                    <div className="pagination-info">
                      <span className="current-page">{currentPage}</span>
                      <span className="page-separator">/</span>
                      <span className="total-pages">{totalPages}</span>
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      다음
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="no-customer-selected">
            <Search size={48} className="no-customer-icon" />
            <p>고객을 검색하여 선택하세요</p>
          </div>
        )}
      </div>

      {/* 배경 오버레이 */}
      {isPanelOpen && (
        <div
          className="panel-overlay"
          onClick={handleClosePanel}
        />
      )}

      {/* 슬라이드 패널 (작성 폼) */}
      <div className={`slide-panel ${isPanelOpen ? 'open' : ''}`}>
        <div className="panel-header">
          <div className="panel-title">
            <PenTool size={24} className="section-icon" />
            <h2>케어 리포트 작성</h2>
          </div>
          <button className="panel-close-btn" onClick={handleClosePanel}>
            <X size={24} />
          </button>
        </div>

        <div className="panel-content">
          {/* 1. 관리 일자 (자동 입력, readOnly) */}
          <div className="form-group">
            <label>
              <Calendar size={18} className="label-icon" />
              관리 일자
            </label>
            <input
              type="date"
              value={careDate}
              readOnly
              className="date-input"
            />
          </div>

          {/* 선택된 고객 표시 */}
          {selectedCustomer && (
            <div className="selected-customer-badge">
              선택된 고객: <strong>{selectedCustomer.name}</strong>
            </div>
          )}

          {/* 패키지 사용 섹션 */}
          {selectedCustomer && (
            <div className="form-group package-section">
              <label className="checkbox-label-with-icon">
                <input
                  type="checkbox"
                  checked={usePackage}
                  onChange={(e) => {
                    setUsePackage(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedPackageId('');
                      setTreatmentCost('');
                      setUsedCount(1);
                    }
                  }}
                />
                <CreditCard size={16} />
                <span>패키지 사용</span>
              </label>

              {usePackage && (
                <div className="package-use-form">
                  {availablePackages.length === 0 ? (
                    <div className="no-packages-warning">
                      <p>⚠️ 사용 가능한 패키지가 없습니다.</p>
                    </div>
                  ) : (
                    <>
                      {/* 사용할 패키지 선택 */}
                      <div className="package-select-group">
                        <label>사용할 패키지</label>
                        <select
                          value={selectedPackageId}
                          onChange={(e) => {
                            setSelectedPackageId(e.target.value);
                            setUsedCount(1); // 패키지 변경 시 횟수 초기화
                          }}
                        >
                          <option value="">선택하세요</option>
                          {availablePackages.map(pkg => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.isShared && `[${pkg.ownerName}님] `}
                              {pkg.packageName} -
                              {pkg.packageType === 'COUNT_BASED'
                                ? ` ${pkg.remainingCount}회 남음`
                                : ` ${pkg.remainingAmount?.toLocaleString()}원 남음`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* 선택된 패키지가 있을 때만 추가 입력 */}
                      {selectedPackageId && getSelectedPackage() && (
                        <>
                          {/* 정액권: 시술 비용 입력 */}
                          {getSelectedPackage().packageType === 'AMOUNT_BASED' && (
                            <div className="cost-input-group">
                              <label>시술 비용</label>
                              <div className="input-with-unit">
                                <input
                                  type="number"
                                  value={treatmentCost}
                                  onChange={(e) => setTreatmentCost(e.target.value)}
                                  placeholder="0"
                                  min="0"
                                  max={getSelectedPackage().remainingAmount}
                                />
                                <span className="unit">원</span>
                              </div>
                            </div>
                          )}

                          {/* 횟수권: 차감 횟수 선택 */}
                          {getSelectedPackage().packageType === 'COUNT_BASED' && (
                            <div className="count-select-group">
                              <label>차감 횟수</label>
                              <div className="input-with-unit">
                                <input
                                  type="number"
                                  min="1"
                                  max={getSelectedPackage().remainingCount}
                                  value={usedCount}
                                  onChange={(e) => setUsedCount(parseInt(e.target.value) || 1)}
                                />
                                <span className="unit">회</span>
                              </div>
                            </div>
                          )}

                          {/* 차감 미리보기 */}
                          <div className="deduct-preview">
                            {getSelectedPackage().packageType === 'COUNT_BASED' ? (
                              <p>
                                <strong>{usedCount}회</strong>가 차감됩니다.
                                (잔여: {getSelectedPackage().remainingCount}회 → {getSelectedPackage().remainingCount - usedCount}회)
                              </p>
                            ) : (
                              treatmentCost && (
                                <p>
                                  <strong>{parseInt(treatmentCost).toLocaleString()}원</strong>이 차감됩니다.
                                  (잔여: {getSelectedPackage().remainingAmount?.toLocaleString()}원 → {(getSelectedPackage().remainingAmount - parseInt(treatmentCost)).toLocaleString()}원)
                                </p>
                              )
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 3. 관리 내용 입력 */}
          <div className="form-group">
            <label>
              <FileText size={18} className="label-icon" />
              관리 내용
            </label>
            <textarea
              rows="8"
              placeholder="오늘의 시술 내용, 피부 상태, 특이사항 등을 기록하세요..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {/* 3-1. 사진 촬영/업로드 (선택 사항) */}
          <div className="form-group package-section">
            <button
              type="button"
              onClick={() => {
                setUsePhoto(!usePhoto);
                if (usePhoto) {
                  setPhotos({ front: null, left: null, right: null });
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                background: usePhoto ? '#f8f9fa' : 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.95rem',
                fontWeight: '500',
                color: '#495057',
                transition: 'all 0.2s'
              }}
            >
              <Camera size={16} />
              <span>관리 사진 첨부</span>
              <span style={{ marginLeft: 'auto' }}>
                {usePhoto ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </span>
            </button>

            {usePhoto && (
              <div className="package-use-form">
                <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                  정면, 좌측, 우측 중 원하는 각도의 사진을 촬영하거나 업로드하세요. 한 장만 올려도 됩니다.
                </p>

                <div className="photo-grid">
                  {/* 정면 사진 */}
                  <div className="photo-item">
                    <p className="photo-label">정면</p>
                    {photos.front ? (
                      <div className="photo-preview">
                        <img src={photos.front} alt="정면" />
                        <button onClick={() => handleDeletePhoto('front')} className="photo-delete-btn">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => handleOpenPhotoSelector('front')} className="photo-add-btn">
                        <Camera size={32} color="#6c757d" />
                        <span className="photo-add-btn-text">추가</span>
                      </button>
                    )}
                  </div>

                  {/* 좌측 사진 */}
                  <div className="photo-item">
                    <p className="photo-label">좌측</p>
                    {photos.left ? (
                      <div className="photo-preview">
                        <img src={photos.left} alt="좌측" />
                        <button onClick={() => handleDeletePhoto('left')} className="photo-delete-btn">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => handleOpenPhotoSelector('left')} className="photo-add-btn">
                        <Camera size={32} color="#6c757d" />
                        <span className="photo-add-btn-text">추가</span>
                      </button>
                    )}
                  </div>

                  {/* 우측 사진 */}
                  <div className="photo-item">
                    <p className="photo-label">우측</p>
                    {photos.right ? (
                      <div className="photo-preview">
                        <img src={photos.right} alt="우측" />
                        <button onClick={() => handleDeletePhoto('right')} className="photo-delete-btn">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => handleOpenPhotoSelector('right')} className="photo-add-btn">
                        <Camera size={32} color="#6c757d" />
                        <span className="photo-add-btn-text">추가</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* 숨겨진 파일 업로드 input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>

          {/* 4. 서명란 */}
          <div className="signature-section">
            {/* 원장 서명 (고정) */}
            <div className="form-group">
              <label>
                <PenTool size={18} className="label-icon" />
                원장 서명
              </label>
              <div className="admin-signature-box">
                <span className="admin-name-signature">{adminName}</span>
              </div>
            </div>

            {/* 고객 서명 패드 */}
            <div className="form-group">
              <label>
                <PenTool size={18} className="label-icon" />
                고객 서명
              </label>
              <div className="signature-container">
                <SignatureCanvas
                  ref={signaturePad}
                  canvasProps={{
                    className: 'signature-canvas'
                  }}
                  penColor="#3d2817"
                />
              </div>
              <button className="btn-secondary" onClick={handleClearSignature}>
                <RotateCcw size={16} />
                서명 초기화
              </button>
            </div>
          </div>

          {/* 5. 저장 버튼 */}
          <div className="form-actions">
            <button className="btn-primary" onClick={handleSave}>
              <Save size={20} />
              저장
            </button>
          </div>
        </div>
      </div>

      {/* 사진 촬영/업로드 선택 모달 */}
      {showPhotoSelector && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPhotoSelector(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '1.5rem', textAlign: 'center', fontSize: '1.25rem', fontWeight: '600' }}>
              사진 추가 방법 선택
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button
                onClick={handleSelectCamera}
                style={{
                  padding: '1rem',
                  border: '2px solid #007bff',
                  borderRadius: '8px',
                  background: '#007bff',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0056b3';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#007bff';
                }}
              >
                <Camera size={20} />
                카메라로 촬영하기
              </button>
              <button
                onClick={handleSelectUpload}
                style={{
                  padding: '1rem',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#6c757d',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#6c757d';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#6c757d';
                }}
              >
                <Upload size={20} />
                기존 이미지 업로드
              </button>
              <button
                onClick={() => setShowPhotoSelector(false)}
                style={{
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#f8f9fa',
                  color: '#6c757d',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e6ea';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 카메라 모달 */}
      {showCamera && (
        <GuideCamera
          onCapture={handlePhotoCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default CareReportPage;
